import { type EntryEventFacts, formatMark } from "@seagames/contracts";
import { useUiStrings } from "@seagames/ui";
import { useTranslation } from "react-i18next";

/**
 * An event's facts as both editors show them (read-only, from the view): the
 * type (Individual / Team of 4), the entries quota ("Up to 2 entries")
 * and the qualifying standard ("Qualifying: 10.75 s or under"
 * — the direction is the event's `lower_is_better`, the mark printed by
 * contracts' `formatMark`, the note in the tooltip).
 */

export function TypeChip({ event: e }: { event: Pick<EntryEventFacts, "competitor_type" | "team_size_min" | "team_size_max"> }) {
	const { t } = useTranslation("entries");
	const { t: ts } = useTranslation("shortlist");
	const { formatNumber: fmt } = useUiStrings();
	if (e.competitor_type === "INDIVIDUAL")
		return (
			<span className="ent-type" data-type="INDIVIDUAL">
				{ts("competitorType.INDIVIDUAL")}
			</span>
		);
	const min = e.team_size_min;
	const max = e.team_size_max;
	const label =
		min !== null && max !== null && min !== max
			? t("event.teamRange", { min: fmt(min), max: fmt(max) })
			: (max ?? min) !== null
				? t("event.teamOf", { n: fmt((max ?? min) as number) })
				: ts("competitorType.TEAM");
	return (
		<span className="ent-type" data-type="TEAM">
			{label}
		</span>
	);
}

/**
 * The entries quota ("Up to 2 entries"), or null when the event has none.
 * The shortlist quota is not shown here (user, 2026-10-30): beside the
 * entries it read as one pool — it belongs to the Shortlist.
 */
export function useQuotaText(): (e: Pick<EntryEventFacts, "max_entries_per_noc">) => string | null {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	return (e) => {
		const en = e.max_entries_per_noc;
		return en === null ? null : t("event.quotaEntries", { count: en, n: fmt(en) });
	};
}

export function QuotaChip({ event: e }: { event: Pick<EntryEventFacts, "max_entries_per_noc"> }) {
	const { t } = useTranslation("entries");
	const text = useQuotaText()(e);
	return (
		<span className="ent-quota" data-none={text === null || undefined} title={t("event.quotaHint")}>
			{text ?? t("event.quotaNone")}
		</span>
	);
}

/** "10.75 s", "7.85 m", "142.5 kg" — the standard with its unit. */
export function useMarkText(): (q: NonNullable<EntryEventFacts["qualifying"]>) => string {
	const { t } = useTranslation("entries");
	return (q) => t(`event.mark.${q.result_type}`, { mark: formatMark(q.result_type, q.mark) });
}

export function QualifyingLine({ event: e }: { event: Pick<EntryEventFacts, "qualifying"> }) {
	const { t } = useTranslation("entries");
	const markText = useMarkText();
	const q = e.qualifying;
	if (!q) return null;
	const mark = markText(q);
	return (
		<span className="ent-qual" title={q.note ?? undefined}>
			{q.lower_is_better ? t("event.qualifyingLower", { mark }) : t("event.qualifyingHigher", { mark })}
			{q.note && <span className="sr-only">. {q.note}</span>}
		</span>
	);
}
